import { useMediaQuery } from '@mui/material';
import { format, intlFormat, startOfToday } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isTodayInRange } from '../../App.util';
import { ModalViews } from '../../constants';
import {
  StockCountItem,
  StockInventoryCountResponse,
  StockInventoryCountUpdatedItems,
  useAsyncAction,
  useRfid,
  useStoreInformation,
} from '../../hooks';
import { usePageEventTracker } from '../../hooks/usePageEventTracker';
import { Drawer, Icon, Icons, Typography } from '../../stories/atoms';
import { ColumnFlexContainer, RowFlexContainer } from '../../stories/atoms/containerV2/ContainerV2';
import { ActionButton, Animation, Animations, Banner, BannerItemsType, Button } from '../../stories/molecules';
import { Page } from '../../stories/organisms';
import { muiTheme } from '../../theme';
import { commonTranslations, yearlyStockTakeTranslations } from '../../translations';
import { getUniqueId, isFailureResponse } from '../../utils';
import { StyledButton, StyledNote } from './YearlyStockTakePage.styles';
import { InventoryCheckModal } from './inventoryCheckModal/InventoryCheckModal';
import { ItemSelectionModal } from './itemSelectionModal/ItemSelectionModal';
import { StockDetailsCard } from './stockDetailsCard/StockDetailsCard';
import { StockDropdown, StockDropdownType } from './stockDropdown/StockDropdown';
import { StockFilter } from './stockFilter/StockFilter';
import { StockTable } from './stockTable/StockTable';

export type CheckedItem = {
  sicScope: string;
  isChecked: boolean;
};

export const YearlyStockTakePage: FC<unknown> = observer(() => {
  usePageEventTracker('yearly_stock_take');

  const { t, i18n } = useTranslation();
  const { close } = commonTranslations;

  const { error, loading, noteText, bannerText, articleBasedOnText, numberOfArticlesText } =
    yearlyStockTakeTranslations;

  const { exclusionListCount } = useStoreInformation();
  const { fetchStockInventoryCount, updateStockInventoryCount } = useRfid();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));

  const [stockData, setStockData] = useState<StockInventoryCountResponse | null>(null);
  const [isInventoryCheckModalOpen, setIsInventoryCheckModalOpen] = useState(false);
  const [isItemSelectionModalOpen, setIsItemSelectionModalOpen] = useState(false);
  const [modalDetails, setModalDetails] = useState<StockCountItem[]>([]);
  const [modalView, setModalView] = useState<ModalViews>(ModalViews.INITIAL);
  const [isBulkAction, setIsBulkAction] = useState(false);
  const [drawerDetails, setDrawerDetails] = useState<StockCountItem | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState<string[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<string[]>([]);
  const [selectedSubindex, setSelectedSubindex] = useState<string[]>([]);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [bannerItems, setBannerItems] = useState<BannerItemsType[]>([]);
  const [checkedItems, setCheckedItems] = useState<CheckedItem[]>([]);

  const { isInRange, endDate: enclusionCountEndDate } = isTodayInRange(exclusionListCount);

  const {
    result: stockInventoryCountData,
    isLoading: stockInventoryCountLoading,
    isError: stockInventoryCountError,
    triggerAction: triggerStockInventoryCount,
  } = useAsyncAction<StockInventoryCountResponse>();

  const {
    result: updatedStockInventoryCountData,
    isLoading: updatedStockInventoryCountLoading,
    triggerAction: triggerUpdatedStockInventoryCount,
  } = useAsyncAction<StockInventoryCountResponse>();

  const fetchData = async () => {
    const formatterDate = format(new Date(), 'yyyy-MM-dd');
    const response = await fetchStockInventoryCount(formatterDate);
    if (isFailureResponse(response)) {
      throw new Error();
    }
    setBannerItems(
      isInRange
        ? [
            {
              id: getUniqueId(),
              label: t(bannerText.key, {
                defaultValue: bannerText.defaultValue,
                endDate: enclusionCountEndDate,
              }),
              type: 'info',
              actionButton: ActionButton.Close,
            },
          ]
        : [],
    );
    return response.data;
  };

  const updateData = async (updatedItems: StockInventoryCountUpdatedItems[]) => {
    const formatterDate = format(new Date(), 'yyyy-MM-dd');
    const response = await updateStockInventoryCount(formatterDate, updatedItems);
    if (isFailureResponse(response)) {
      setModalView(ModalViews.ERROR);
      throw new Error();
    }
    setModalView(ModalViews.SUCCESS);
    return response.data;
  };

  useEffect(() => {
    triggerStockInventoryCount(fetchData);
  }, []);

  useEffect(() => {
    if (stockInventoryCountData) {
      setStockData(stockInventoryCountData);
    }
  }, [stockInventoryCountData]);

  useEffect(() => {
    if (updatedStockInventoryCountData) {
      setStockData(updatedStockInventoryCountData);
    }
  }, [updatedStockInventoryCountData]);

  useEffect(() => {
    if (stockData) {
      setCheckedItems(
        stockData.counts.map((item) => ({
          sicScope: item.sicScope,
          isChecked: false,
        })),
      );
    }
  }, [stockData]);

  useEffect(() => {
    if (!isInventoryCheckModalOpen || !isItemSelectionModalOpen) {
      setModalView(ModalViews.INITIAL);
    }
  }, [isInventoryCheckModalOpen, isItemSelectionModalOpen]);

  const handleModalDetails = (sicScope: string) => {
    if (!!stockData?.counts.length) {
      setIsInventoryCheckModalOpen(true);
      const article = stockData.counts.filter((item) => item.sicScope === sicScope);
      setModalDetails(article);
    }
  };

  const handleUpdateStock = (updatedItems: StockInventoryCountUpdatedItems[]) => {
    triggerUpdatedStockInventoryCount(() => updateData(updatedItems));
  };

  const handleCheckedItems = (sicScope: string, isChecked: boolean) => {
    const updatedArray = [...checkedItems];
    updatedArray.forEach((item) => {
      if (item.sicScope === sicScope) {
        item.isChecked = isChecked;
      }
    });
    setCheckedItems(updatedArray);
  };

  // const applyChanges = () => {
  //   const filteredArray = stockData?.counts.filter((item) => {
  //     const changedItem = checkedItems.find((checked) => checked.sicScope === item.sicScope);
  //     return changedItem?.isChecked;
  //   });
  //   setModalDetails(filteredArray ?? []);
  //   setIsItemSelectionModalOpen(true);
  // };

  // const handleReset = () => {
  //   const updatedArray = [...checkedItems];
  //   updatedArray.forEach((item) => {
  //     item.isChecked = false;
  //   });
  //   setCheckedItems(updatedArray);
  // };

  const handleResetFilters = () => {
    setSelectedDepartment([]);
    setSelectedIndex([]);
    setSelectedSubindex([]);
  };

  const handleRemoveItem = (item: StockCountItem) => {
    setModalDetails((prev) => prev.filter((stockItem) => stockItem.sicScope !== item.sicScope));
  };

  const hasSelectedFilters = !!selectedDepartment.length || !!selectedIndex.length || !!selectedSubindex.length;

  const noData = !stockData?.counts.length;

  const filteredStockCount = stockData?.counts.filter((item) => {
    return (
      (selectedDepartment.length === 0 || selectedDepartment.includes(item.departmentName)) &&
      (selectedIndex.length === 0 || selectedIndex.includes(item.index)) &&
      (selectedSubindex.length === 0 || selectedSubindex.includes(item.subIndex))
    );
  });
  const allSelectedItems = [...selectedDepartment, ...selectedIndex, ...selectedSubindex];

  const noDataForSelectedFilters = filteredStockCount
    ? filteredStockCount.length === 0 && allSelectedItems.length > 0
    : false;

  return (
    <Page
      header={
        <RowFlexContainer padding={isMobile ? [0, 4] : [2, 8]}>
          <StockDropdown
            stockCount={stockData?.counts || []}
            disabled={stockInventoryCountLoading || stockInventoryCountError || noData}
            selectedDepartment={selectedDepartment}
            selectedIndex={selectedIndex}
            selectedSubindex={selectedSubindex}
            isFilterDrawerOpen={isFilterDrawerOpen}
            onClose={() => setIsFilterDrawerOpen(false)}
            clearAllFilters={handleResetFilters}
            handleDropdown={(type, selectedItems) => {
              if (type === StockDropdownType.DEPARTMENT) {
                setSelectedDepartment(selectedItems);
              } else if (type === StockDropdownType.INDEX) {
                setSelectedIndex(selectedItems);
              } else {
                setSelectedSubindex(selectedItems);
              }
            }}
          />
          {isMobile && (
            <Button
              label={t(commonTranslations.allFilters.key, commonTranslations.allFilters.defaultValue)}
              buttonContentProps={{
                labelPosition: 'left',
                iconOptions: {
                  icon: Icons.FILTER,
                  size: 'small',
                },
                textOptions: {
                  whiteSpace: 'nowrap',
                },
              }}
              aria-haspopup="true"
              aria-expanded={isFilterDrawerOpen ? 'true' : undefined}
              onClick={() => setIsFilterDrawerOpen(true)}
              variant="text"
              disabled={stockInventoryCountLoading || stockInventoryCountError || noData}
            />
          )}
        </RowFlexContainer>
      }
      body={
        <ColumnFlexContainer padding={isMobile ? [4, 3] : [4, 8]} data-testid="yearly-stock-take-page">
          <Banner isSticky items={bannerItems} onChange={() => setBannerItems([])} zIndex={[25]} />
          {stockInventoryCountLoading && (
            <Animation title={t(loading.key, loading.defaultValue)} animation={Animations.LOADING} />
          )}
          {!stockInventoryCountLoading && stockInventoryCountError && (
            <Animation
              title={t(error.title.key, error.title.defaultValue)}
              subtitle={t(error.subTitle.key, error.subTitle.defaultValue)}
              animation={Animations.SAD}
            />
          )}

          {noData && !stockInventoryCountLoading && !stockInventoryCountError && (
            <Animation
              title={t(
                yearlyStockTakeTranslations.noData.title.key,
                yearlyStockTakeTranslations.noData.title.defaultValue,
              )}
              subtitle={t(
                yearlyStockTakeTranslations.noData.subtitle.key,
                yearlyStockTakeTranslations.noData.subtitle.defaultValue,
              )}
              animation={Animations.CONFUSED}
            />
          )}

          {!stockInventoryCountLoading && !stockInventoryCountError && !noData && stockData && (
            <div data-testid="yearly-stock-take-container">
              {isMobile && drawerDetails && (
                <Drawer
                  variant="temporary"
                  position="bottom"
                  isOpen={isDrawerOpen}
                  onClose={() => setIsDrawerOpen(false)}
                  hasBorderRadius
                >
                  <ColumnFlexContainer padding={[4, 6, 2]} justifyContent="between" data-testid="drawer-container">
                    <StockDetailsCard
                      isBulkAction={isBulkAction}
                      stockCountItem={drawerDetails}
                      hasborder={false}
                      status={drawerDetails.status}
                      isDrawerContent
                    />
                    <StyledButton
                      label={t(close.key, close.defaultValue)}
                      data-testid="close-drawer"
                      textOptions={{
                        type: 'button',
                        color: 'white',
                      }}
                      margin={[4, 0, 2]}
                      hasRadius
                      onClick={() => setIsDrawerOpen(false)}
                    />
                  </ColumnFlexContainer>
                </Drawer>
              )}
              {isInventoryCheckModalOpen && (
                <InventoryCheckModal
                  isOpen={isInventoryCheckModalOpen}
                  onClose={() => setIsInventoryCheckModalOpen(false)}
                  stockModalDetails={modalDetails}
                  modalView={modalView}
                  isLoading={updatedStockInventoryCountLoading}
                  updateStock={(updatedItems) => handleUpdateStock(updatedItems)}
                />
              )}

              {isItemSelectionModalOpen && (
                <ItemSelectionModal
                  isOpen={isItemSelectionModalOpen}
                  onClose={() => setIsItemSelectionModalOpen(false)}
                  stockModalDetails={modalDetails}
                  modalView={modalView}
                  isLoading={updatedStockInventoryCountLoading}
                  updateStock={(updatedItems) => handleUpdateStock(updatedItems)}
                  removeItem={(item) => handleRemoveItem(item)}
                />
              )}
              {isMobile && (
                <StyledNote>
                  <Icon icon={Icons.WARNING} margin={[0, 2, 0, 0]} size="small" />
                  <Typography>{t(noteText.key, noteText.defaultValue)}</Typography>
                </StyledNote>
              )}
              <RowFlexContainer justifyContent="between" margin={[0, 0, 3]}>
                <Typography>
                  {t(articleBasedOnText.key, articleBasedOnText.defaultValue)}:{' '}
                  {intlFormat(startOfToday(), {}, { locale: i18n.language })}
                </Typography>
                <Typography>
                  {t(numberOfArticlesText.key, numberOfArticlesText.defaultValue)}: {stockData?.counts.length}
                </Typography>
              </RowFlexContainer>
              {!isMobile && hasSelectedFilters && (
                <StockFilter
                  selectedDepartment={selectedDepartment}
                  selectedIndex={selectedIndex}
                  selectedSubindex={selectedSubindex}
                  onClose={(type, removedItems) => {
                    if (type === StockDropdownType.DEPARTMENT) {
                      setSelectedDepartment(selectedDepartment.filter((item) => !removedItems.includes(item)));
                    } else if (type === StockDropdownType.INDEX) {
                      setSelectedIndex(selectedIndex.filter((item) => !removedItems.includes(item)));
                    } else {
                      setSelectedSubindex(selectedSubindex.filter((item) => !removedItems.includes(item)));
                    }
                  }}
                  onReset={handleResetFilters}
                />
              )}
              <StockTable
                stockCount={filteredStockCount || []}
                bannerItems={bannerItems}
                noData={noDataForSelectedFilters}
                checkedItems={checkedItems}
                handleCheckedItems={(sicScope, isChecked) => {
                  handleCheckedItems(sicScope, isChecked);
                }}
                handleModalDetails={(sicScope) => handleModalDetails(sicScope)}
                handleViewDetails={(item) => {
                  setIsBulkAction(false);
                  setDrawerDetails(item);
                  setIsDrawerOpen(true);
                }}
              />
              {/* Hiding this for card: BTCOBUI-4350 */}
              {/* {!noDataForSelectedFilters && (
                <RowFlexContainer justifyContent="end" margin={[3, 0, 0]} gap={[4]}>
                  <Button
                    data-testid="reset-button"
                    label={t(resetText.key, resetText.defaultValue)}
                    fullWidth={isMobile}
                    variant="secondary"
                    onClick={handleReset}
                    disabled={!checkedItems.some((item) => item.isChecked)}
                  />
                  <Button
                    data-testid="apply-changes-button"
                    label={t(applyChangesText.key, applyChangesText.defaultValue)}
                    fullWidth={isMobile}
                    onClick={applyChanges}
                    disabled={!checkedItems.some((item) => item.isChecked)}
                  />
                </RowFlexContainer>
              )} */}
            </div>
          )}
        </ColumnFlexContainer>
      }
    />
  );
});
